import {Component, OnInit, OnDestroy, Input, Optional, Inject, EventEmitter, Output} from '@angular/core';
import { Account, TradingDayInfo } from './../../models/common/trading-common.model';
import { Subscription, interval } from 'rxjs';
import { TradingService } from './../../services/common/trading-service.service';
import { LogService } from '@pershing/pershing-core';
import { Balances, defaultBalances } from './../../models/balances/index';
import { getElapsedTimeString } from './../../utils/balances/timer.util';
import { BalancesConstants } from '../../utils/balances/balances-constants';
import { HttpResponse } from '@angular/common/http';
import { BalancesMapperUtil } from '../../utils/balances/balances-mapper.util';
import { BalancesService } from './../../services/balances/balances-service.service';
import { MatDialog } from '@angular/material/dialog';
import { TradingEntitlements } from '../../models';
import { BalancesMobileDialog } from '../mobile/balances-dialog.component';
import { OrderSettingsComponent } from '../common/order-settings/order-settings.component';
import { registerLocaleData } from '@angular/common';
import es from '@angular/common/locales/es';


@Component({
    selector: 'balances-component',
    templateUrl: './balances.component.html',
    styleUrls: ['./balances.component.css']
})

/**
 * The Balances Summary component to take care of rendering the balances Summary for the selected account.
 */
export class BalancesComponent implements OnInit, OnDestroy {
    @Input() isMobileDialog: boolean = false;
    @Input() isMobileView: string = 'false';
    @Output() openDialogEmitter = new EventEmitter<string>();
    platform: string;
    fundsAvailableToTrade: number;
    totalAccountWorthValue: number;
    balancesResultSubscription: Subscription;
    accountSubscription: Subscription;
    timerSubscription: Subscription;
    tradingDayInfoSubscription: Subscription;
    onEntitlementsSubscription: Subscription;
    isChangeOrCorrectOrderSubscription: Subscription;
    account: Account;
    showBalances = false;
    showLastRefreshed = false;
    showSpin = false;
    systemError: boolean = false;
    mobView: boolean = false;
    isExtendedHours: boolean = false;
    isAvgPriceOrAllocationAcct: boolean = false;
    errorKey: string;
    clock: string;
    quoteType = '';
    isLoading: boolean = true;
    showTotalAccountWorthValue: boolean = false;
    activeTheme: string;
    isProfessionalUser: boolean = true;
    isChangeOrCorrectOrder: boolean = false;
    language: string = '';


    constructor(
        private balancesService: BalancesService,
        private tradingService: TradingService,
        public dialog: MatDialog,
        private logService: LogService,
        @Optional() @Inject('AppSettings') appSettings: any) {
        if (appSettings) {
            this.platform = appSettings.platform;
            this.language = appSettings.language;
            if (this.language === 'es') {
                registerLocaleData( es );
            }
        }
    }

    ngOnInit() {
        this.onEntitlementsSubscription = this.tradingService.onEntitlementsSubject$.subscribe(entResp => {
            if (entResp?.errorCode === 0) {
                this.isProfessionalUser = (entResp.response as TradingEntitlements).isProfessionalUser;
                this.activeTheme = this.tradingService.getActiveThemeFromCurrentContainer();
            }
        });
        this.accountSubscription = this.tradingService.accountSubject$.subscribe((data: Account) => this.onAccount(data));
        this.tradingDayInfoSubscription = this.tradingService.tradingDayInfoSubject$.subscribe((tradingDayInfo: TradingDayInfo) => {
            // Reload the getTradingDay info to check if we need to enable the extended hours.
            this.isExtendedHours =  tradingDayInfo?.isECNPostTradingHrs || tradingDayInfo?.isECNPreTradingHrs;
        });
        this.isChangeOrCorrectOrderSubscription = this.tradingService.isChangeOrCorrectOrder$.subscribe((isChangeOrCorrectOrder: boolean) => {
            this.isChangeOrCorrectOrder = isChangeOrCorrectOrder === null ? false : isChangeOrCorrectOrder;
        });
        if (this.platform && this.platform === 'professional') {
            this.showTotalAccountWorthValue = true;
        }
   }
   
    onAccount(accountSelected: Account): void {
        this.showBalances = false;
        this.systemError = false;
        if (!!accountSelected && accountSelected.acctNum) {
            this.account = accountSelected;
            this.isAvgPriceOrAllocationAcct =
            this.account.acctNum.toString().substring(0, 3) === '950' || this.account.acctNum.toString().substring(3, 6) === '897' ||
                this.account.acctNum.toString().substring(3, 6) === '896';
            if (!this.isAvgPriceOrAllocationAcct) {
                this.fetchBalances();
            }
        }
    }

    
    // Fetches holdings for the selected account on Account change.
    fetchBalances(): void {
        this.showSpin = true;
        this.balancesResultSubscription = this.balancesService.getBalances(this.account.acctNum, this.quoteType).
            subscribe(success => {
                if (success) {
                    this.balancesSuccessCallback(success);
                }
            },
                error => this.balancesErrorCallback(error)
            );
    }

    balancesSuccessCallback(response: HttpResponse<any>): void {
        this.isLoading=false;
        let data = response.body;
        this.showSpin = false;

        if (data.errorCode === 0 && data.response) {
            this.showBalances = true;
            this.systemError = false;
            let balancesResult: Balances = defaultBalances();
            balancesResult.balancesSummary = BalancesMapperUtil.getBalancesSummaryFromResponse(data.response.balances.balancesSummary);
            this.clearBalances();
            this.startCounter();
            this.fundsAvailableToTrade = balancesResult.balancesSummary.fundsAvailableToTrade;
            this.totalAccountWorthValue = balancesResult.balancesSummary.totalAccountWorth.value;
        } else {
            this.clearBalances();
            this.showBalances = false;
            this.systemError = true;
            this.errorKey = 'errors.SYSTEM_ERROR_MESSAGE';
            this.logService.error(BalancesConstants.DATA_NOT_LOADING);
        }
    }

    startCounter(): void {
        let counter = interval(1000);
        this.timerSubscription = counter.subscribe(val => {
            this.clock = getElapsedTimeString(val, true);
            this.showLastRefreshed = true;
        });
    }

    private balancesErrorCallback(error: any): void {
        console.log('balancesErrorCallback : ', error);
        this.logService.error('onBalancesErrorCallback : ', error);
        this.showSpin = false;
        this.showBalances = false;
        this.systemError = true;
        this.isLoading=false;
        this.errorKey = 'errors.HTTP_ERROR';
    }

    private clearBalances(): void {
        if ( this.timerSubscription ) {
            this.timerSubscription.unsubscribe();
        }

        this.showLastRefreshed = false;
        this.clock = '';
        this.fundsAvailableToTrade = undefined;
        this.totalAccountWorthValue =  undefined;
    }

    openDialog(): void {
        this.dialog.open(BalancesMobileDialog, {
            disableClose: true,
            autoFocus: false,
            panelClass: 'modal',
            width: '75%',
            data: {
            }
        });
    }
    
    public openOrderSettings() {
        this.openOrderSettingsBalances(!!this.tradingService.tradingEntitlements?.isSettingsAdminViewEnabled, this.account, this.dialog, this.logService);
    }

    openOrderSettingsBalances(isAdmin: boolean, _account: Account, _dialog: MatDialog, _logService: LogService) {
        let isWindowBigSize = window.innerWidth > 1280;
        let dialogWidth = '80%';
        if (isAdmin && isWindowBigSize || (!isAdmin && !isWindowBigSize)) {
            dialogWidth = '60%';
        } else if (!isAdmin && isWindowBigSize) {
            dialogWidth = '40%';
        }
        const orderSettingsDialogRef = _dialog.open(OrderSettingsComponent, {
            panelClass: 'modal',
            disableClose: true,
            autoFocus: false,
            width: dialogWidth,
            data: {
                account: _account
            }
        });
    
        orderSettingsDialogRef.afterClosed().subscribe((data) => {
            _logService.log('The orderSettingsDialogRef was closed', data);
        });
    }

    ngOnDestroy(): void {
        if ( this.balancesResultSubscription ) {
            this.balancesResultSubscription.unsubscribe();
        }

        if ( this.accountSubscription ) {
            this.accountSubscription.unsubscribe();
        }

        if ( this.timerSubscription ) {
            this.timerSubscription.unsubscribe();
        }

        if ( this.tradingDayInfoSubscription ) {
            this.tradingDayInfoSubscription.unsubscribe();
        }

        if ( this.onEntitlementsSubscription ) {
            this.onEntitlementsSubscription.unsubscribe();
        }

        if ( this.isChangeOrCorrectOrderSubscription ) {
            this.isChangeOrCorrectOrderSubscription.unsubscribe();
        }
    }
}
